import React, {useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import s from './AlertMessage.module.scss'
import {ReactComponent as AlarmIcon} from "../../assets/icons/alarm.svg";
import {getAlertLevelTone} from '../../utils/helpers/alarms';
import useReasonPages from './useReasonPages';
import useAlertRotation from './useAlertRotation';

const AlertMessage = ({activeAlerts = [], activeAlertLevels = []}) => {
    const {t, i18n} = useTranslation();
    const viewportRef = useRef(null);
    const measureRef = useRef(null);
    const pagesByReason = useReasonPages(activeAlertLevels.map(level => level.reason || ''), viewportRef, measureRef);
    const entries = useMemo(() => {
        const occurrences = new Map();
        return activeAlertLevels.map(level => {
            // Timestamps may change independently of the reason being read.
            const identity = JSON.stringify([level.alertLevel, level.reason]);
            const occurrence = occurrences.get(identity) || 0;
            occurrences.set(identity, occurrence + 1);
            return {...level, key: `${identity}:${occurrence}`, pages: pagesByReason.get(level.reason || '') || [level.reason || '']};
        });
    }, [activeAlertLevels, pagesByReason]);
    const detailCount = activeAlertLevels.length;

    const tone = activeAlertLevels.some(level => getAlertLevelTone(level.alertLevel) === 'red')
        ? 'red'
        : activeAlertLevels.some(level => getAlertLevelTone(level.alertLevel) === 'yellow') ? 'yellow' : 'default';
    const {index: currentIndex, page} = useAlertRotation(entries, tone === 'red' ? 2 : tone === 'yellow' ? 1 : 0);
    const detail = entries[currentIndex];
    const levelLabel = level => {
        const levelTone = getAlertLevelTone(level);
        return levelTone !== 'default'
            ? t(`alarm.level.${levelTone}`)
            : level ? t('alarm.level.other', {level}) : t('alarm.level.unavailable');
    };
    const createdAt = detail?.createdAt ? new Date(detail.createdAt) : null;
    const validCreatedAt = createdAt && !Number.isNaN(createdAt.getTime());
    const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'uk-UA';

    return (
        <section className={`${s.AlertMessage} ${s[tone]}`} role="alert" aria-atomic="true">
            <div className={`${s.heading} ${activeAlerts.length > 2 ? s.compactHeading : ''}`}>
                <AlarmIcon aria-hidden="true" />
                <h2>{activeAlerts.map(type => t(type, {defaultValue: t('UNKNOWN')})).join(' · ')}</h2>
            </div>
            {tone !== 'default' && <div className={s.primaryLevel}>{t(`alarm.level.${tone}`)}</div>}
            <p className={s.instruction}>{t('alarm.shelterNow')}</p>
            {detail && (
                <div className={s.detail}>
                    <div className={s.detailHeader}>
                        <span className={`${s.levelBadge} ${s[getAlertLevelTone(detail.alertLevel)]}`}>
                            {levelLabel(detail.alertLevel)}
                        </span>
                        {validCreatedAt && (
                            <time dateTime={detail.createdAt}>
                                {t('alarm.since', {time: createdAt.toLocaleString(locale, {
                                    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                                })})}
                            </time>
                        )}
                        {detailCount > 1 && (
                            <span className={s.counter} aria-label={t('alarm.detailCounter', {current: currentIndex + 1, total: detailCount})}>
                                {currentIndex + 1} / {detailCount}
                            </span>
                        )}
                    </div>
                    <div className={s.reasonViewport} ref={viewportRef}>
                        <p className={s.reason}>{detail.pages[page]}</p>
                        <p className={`${s.reason} ${s.measure}`} ref={measureRef} aria-hidden="true" />
                    </div>
                    <div className={s.pageCounter}>
                        {detail.pages.length > 1 && t('alarm.reasonPage', {current: page + 1, total: detail.pages.length})}
                    </div>
                </div>
            )}
        </section>
    );
};

export default AlertMessage;
